import { AuthResult } from '@ionic-enterprise/auth';
import { BrowserVaultObfuscatory } from './decorator';
import { Capacitor } from '@capacitor/core';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { UnlockMode } from './unlock-mode.type';
import { UserData } from '../user';
import { VaultService } from './vault.service';
import { VaultServiceInterface } from './interfaces';

@Injectable({ providedIn: 'root' })
export class VaultFacade implements VaultServiceInterface {
  private readonly vaultService = inject(VaultService);
  private readonly vault = Capacitor.isNativePlatform()
    ? this.vaultService
    : new BrowserVaultObfuscatory(this.vaultService);

  public get locked(): Observable<boolean> {
    return this.vault.locked;
  }

  public get vaultReady(): Observable<boolean> {
    return this.vault.vaultReady;
  }

  public canUnlock(): Promise<boolean> {
    return this.vault.canUnlock();
  }

  public clear(): Promise<void> {
    return this.vault.clear();
  }

  public clearSession(): Promise<void> {
    return this.vault.clearSession();
  }

  public async getLastUnlockMode(): Promise<UnlockMode> {
    return await this.vault.getLastUnlockMode();
  }

  public getSession(): Promise<AuthResult | null> {
    return this.vault.getSession();
  }

  public getUserData(): Promise<UserData | null> {
    return this.vault.getUserData();
  }

  public getValue<T>(key: string): Promise<T | null> {
    return this.vault.getValue<T>(key);
  }

  public hasDeviceSecurityEnabled(): Promise<boolean> {
    return this.vault.hasDeviceSecurityEnabled();
  }

  public init(appId?: string): Promise<void> {
    return this.vault.init(appId || '');
  }

  public isEmpty(): Promise<boolean> {
    return this.vault.isEmpty();
  }

  public isLocked(): Promise<boolean> {
    return this.vault.isLocked();
  }

  public lockVault(): Promise<void> {
    return this.vault.lockVault();
  }

  public neverLock(): Promise<boolean> {
    return this.vault.neverLock();
  }

  public provision(): Promise<void> {
    return this.vault.provision();
  }

  public removeValue(key: string): Promise<void> {
    return this.vault.removeValue(key);
  }

  public saveUserDataFromAuthResult(authResult: AuthResult): Promise<void> {
    return this.vault.saveUserDataFromAuthResult(authResult);
  }

  public async setLastUnlockMode(value: UnlockMode): Promise<void> {
    await this.vault.setLastUnlockMode(value);
  }

  public setSession(authResult: AuthResult): Promise<void> {
    return this.vault.setSession(authResult);
  }

  public setValue<T>(key: string, value: T): Promise<void> {
    return this.vault.setValue<T>(key, value);
  }

  public toggleLockAfterBackgrounded(enabled: boolean): Promise<void> {
    return this.vault.toggleLockAfterBackgrounded(enabled);
  }

  public unlockVault(): Promise<void> {
    return this.vault.unlockVault();
  }

  public updateUnlockMode(mode: UnlockMode): Promise<void> {
    return this.vault.updateUnlockMode(mode);
  }
}
